import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusIcon, EditIcon, EyeIcon, DownloadIcon, DollarSignIcon, CalendarIcon, FileTextIcon, Loader2Icon, CheckCircleIcon, CreditCardIcon, AlertTriangleIcon, ClockIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { getInvoiceDisplayStatus, getInvoiceStatusColor, getInvoiceStatusLabel } from '../lib/invoiceStatus'
import { downloadInvoicePdf } from '../lib/downloadInvoicePdf'
import { useDivision } from '../contexts/DivisionContext'
import { matchesDivisionWithFallback } from '../lib/divisionFiltering'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileFilterBar,
  MobileSelect,
  MobileStatStrip,
  mobile,
} from '../components/mobile'

interface InvoiceRecord {
  id: string
  invoice_number: string
  customer_id: string
  total: number
  amount_paid: number
  balance_due: number
  status: string
  invoice_date: string
  due_date: string
  notes?: string
  created_at: string
  operational_division_id?: string | null
  service_type?: string | null
  contract?: { operational_division_id?: string | null; service_type?: string | null } | null
  customer?: { company_name?: string; contact_first_name?: string; contact_last_name?: string }
}

export function Billing() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [activeTab, setActiveTab] = useState<'invoices' | 'aging'>('invoices')
  const [applyingLateFees, setApplyingLateFees] = useState(false)

  useEffect(() => {
    fetchInvoices()
  }, [currentDivision])

  const fetchInvoices = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('invoices')
        .select('*, customer:customers(company_name, contact_first_name, contact_last_name), contract:contracts(operational_division_id, service_type)')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Status is payment-driven (set by record_invoice_payment/Send actions);
      // getInvoiceDisplayStatus only derives the 'past_due' display state.
      const processed = (data || []).map((inv: any) => {
        const total = Number(inv.total || inv.total_amount || inv.subtotal || 0)
        const amount_paid = Number(inv.amount_paid || 0)
        const balance_due = Number(inv.balance_due ?? (total - amount_paid))
        return {
          ...inv,
          contract: Array.isArray(inv.contract) ? inv.contract[0] || null : inv.contract,
          total,
          amount_paid,
          balance_due,
          invoice_date: inv.invoice_date || inv.issue_date || inv.created_at,
          status: getInvoiceDisplayStatus({ status: inv.status, balance_due, due_date: inv.due_date }),
        }
      }).filter((inv: InvoiceRecord) =>
        matchesDivisionWithFallback(inv, currentDivision, inv.contract)
      )

      setInvoices(processed)
    } catch (error) {
      console.error('Error fetching invoices:', error)
      setInvoices([])
    } finally {
      setLoading(false)
    }
  }

  const getCustomerName = (inv: InvoiceRecord) => {
    return inv.customer?.company_name ||
      `${inv.customer?.contact_first_name || ''} ${inv.customer?.contact_last_name || ''}`.trim() ||
      'Unknown Customer'
  }

  const BILLING_COLUMNS = [
    { id: 'invoice_number', label: 'Invoice #' },
    { id: 'customer', label: 'Customer' },
    { id: 'amount', label: 'Amount' },
    { id: 'paid', label: 'Paid' },
    { id: 'balance', label: 'Balance' },
    { id: 'status', label: 'Status' },
    { id: 'due_date', label: 'Due Date' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(BILLING_COLUMNS, { storageKey: 'billing' })

  const filteredInvoices = invoices.filter(inv => {
    const customerName = getCustomerName(inv)
    const matchesSearch = (inv.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customerName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const totalAmount = invoices.reduce((sum, inv) => sum + Number(inv.total || 0), 0)
  const paidAmount = invoices.filter(i => i.status === 'paid').reduce((sum, inv) => sum + Number(inv.total || 0), 0)
  const openAmount = invoices.filter(i => i.status === 'sent').reduce((sum, inv) => sum + Number(inv.balance_due || 0), 0)
  const pastDueAmount = invoices.filter(i => i.status === 'past_due').reduce((sum, inv) => sum + Number(inv.balance_due || 0), 0)
  const invoiceCount = invoices.length
  const paidCount = invoices.filter(i => i.status === 'paid').length

  const agingByCustomer = useMemo(() => {
    const now = new Date()
    const map = new Map<string, {
      customerId: string
      name: string
      current: number
      d1_30: number
      d31_60: number
      d61_90: number
      d90_plus: number
      total: number
    }>()

    invoices.forEach((inv) => {
      const balance = Number(inv.balance_due || 0)
      if (balance <= 0 || inv.status === 'draft') return

      const key = inv.customer_id
      if (!map.has(key)) {
        map.set(key, {
          customerId: key,
          name: getCustomerName(inv),
          current: 0,
          d1_30: 0,
          d31_60: 0,
          d61_90: 0,
          d90_plus: 0,
          total: 0,
        })
      }
      const bucket = map.get(key)!
      const dueDate = inv.due_date ? new Date(inv.due_date) : null
      const daysPastDue = dueDate ? Math.floor((now.getTime() - dueDate.getTime()) / 86400000) : -1

      if (daysPastDue <= 0) bucket.current += balance
      else if (daysPastDue <= 30) bucket.d1_30 += balance
      else if (daysPastDue <= 60) bucket.d31_60 += balance
      else if (daysPastDue <= 90) bucket.d61_90 += balance
      else bucket.d90_plus += balance
      bucket.total += balance
    })

    return Array.from(map.values()).sort((a, b) => b.total - a.total)
  }, [invoices])

  const agingTotals = agingByCustomer.reduce(
    (acc, b) => ({
      current: acc.current + b.current,
      d1_30: acc.d1_30 + b.d1_30,
      d31_60: acc.d31_60 + b.d31_60,
      d61_90: acc.d61_90 + b.d61_90,
      d90_plus: acc.d90_plus + b.d90_plus,
      total: acc.total + b.total,
    }),
    { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0, total: 0 }
  )

  const money = (n: number) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const handleApplyLateFees = async () => {
    if (!window.confirm('Apply late fees to all overdue invoices with a contract late fee percentage?')) return
    try {
      setApplyingLateFees(true)
      const { data, error } = await supabase.rpc('apply_late_fees')
      if (error) throw error
      const count = typeof data === 'number' ? data : 0
      alert(count > 0 ? `Applied late fees to ${count} invoice(s).` : 'No overdue invoices needed a late fee.')
      await fetchInvoices()
    } catch (err: any) {
      alert('Failed to apply late fees: ' + (err.message || 'Unknown error'))
    } finally {
      setApplyingLateFees(false)
    }
  }

  const handleDownloadInvoicePdf = async (inv: InvoiceRecord) => {
    try {
      let lineItems: { description: string; quantity: number; unit_price: number; total: number }[] = []
      const { data: items1 } = await supabase
        .from('invoice_items')
        .select('description, quantity, unit_price, total')
        .eq('invoice_id', inv.id)
        .order('display_order', { ascending: true })
      if (items1 && items1.length > 0) {
        lineItems = items1
      } else {
        const { data: items2 } = await supabase
          .from('invoice_line_items')
          .select('description, quantity, unit_price, total')
          .eq('invoice_id', inv.id)
        if (items2) lineItems = items2
      }

      await downloadInvoicePdf(
        {
          invoiceNumber: inv.invoice_number,
          invoiceDate: inv.invoice_date,
          dueDate: inv.due_date,
          status: getInvoiceStatusLabel(inv.status),
          customerName: getCustomerName(inv),
          lineItems: lineItems.length > 0 ? lineItems : [{ description: 'Invoice total', quantity: 1, unit_price: inv.total, total: inv.total }],
          subtotal: inv.total,
          amountPaid: inv.amount_paid,
          balanceDue: inv.balance_due,
          notes: inv.notes,
        },
        inv.invoice_number
      )
    } catch (err: any) {
      alert('Failed to generate PDF: ' + (err.message || 'Unknown error'))
    }
  }

  const fmtMoney = (n: number) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  return (
    <MobilePage>
      <MobilePageHeader
        title="Billing"
        subtitle={<span className="hidden sm:inline">Manage invoices and billing operations</span>}
        actions={
          <button
            type="button"
            onClick={() => navigate('/invoice-generation')}
            className={mobile.primaryBtn}
          >
            <PlusIcon className="w-4 h-4" />
            <span className="sm:hidden">New</span>
            <span className="hidden sm:inline">Create Invoice</span>
          </button>
        }
      />

      <div className="lg:hidden">
        <MobileStatStrip
          stats={[
            { key: 'total', label: 'Invoiced', value: fmtMoney(totalAmount), tone: 'blue' },
            { key: 'paid', label: 'Paid', value: fmtMoney(paidAmount), tone: 'green' },
            { key: 'sent', label: 'Sent', value: fmtMoney(openAmount), tone: 'amber' },
            { key: 'past', label: 'Past due', value: fmtMoney(pastDueAmount), tone: 'red' },
          ]}
        />
      </div>

      <div className="hidden lg:grid grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Invoiced</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{fmtMoney(totalAmount)}</p>
              <p className="text-xs text-gray-500 mt-1">{invoiceCount} invoice{invoiceCount !== 1 ? 's' : ''}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <DollarSignIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Paid</p>
              <p className="mt-2 text-2xl font-bold text-green-700">{fmtMoney(paidAmount)}</p>
              <p className="text-xs text-gray-500 mt-1">{paidCount} paid</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <CheckCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Sent</p>
              <p className="mt-2 text-2xl font-bold text-yellow-700">{fmtMoney(openAmount)}</p>
              <p className="text-xs text-gray-500 mt-1">{invoices.filter(i => i.status === 'sent').length} sent</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
              <CalendarIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Past due</p>
              <p className="mt-2 text-2xl font-bold text-red-700">{fmtMoney(pastDueAmount)}</p>
              <p className="text-xs text-gray-500 mt-1">{invoices.filter(i => i.status === 'past_due').length} past due</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 text-red-600">
              <FileTextIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      <MobileFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search invoices..."
        className="lg:p-6"
      >
        <MobileSelect
          id="billing-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
          <option value="past_due">Past due</option>
        </MobileSelect>
        <div className="hidden lg:block">
          <ColumnSelector
            columns={BILLING_COLUMNS}
            visibleColumns={visibleColumns}
            onToggle={toggleColumn}
          />
        </div>
        <button
          type="button"
          onClick={handleApplyLateFees}
          disabled={applyingLateFees}
          className={`${mobile.secondaryBtn} border-red-300 text-red-700 hover:bg-red-50`}
          title="Apply late fees to overdue invoices per contract terms"
        >
          {applyingLateFees ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <AlertTriangleIcon className="w-4 h-4" />}
          <span className="hidden sm:inline">Late Fees</span>
        </button>
        <button
          type="button"
          onClick={() => navigate('/billing/ready')}
          className={mobile.successBtn}
        >
          <CheckCircleIcon className="w-4 h-4" />
          Ready to Bill
        </button>
      </MobileFilterBar>

      <div className="border-b border-gray-200 -mx-3 sm:-mx-4 px-3 sm:px-4 overflow-x-auto">
        <nav className="-mb-px flex gap-4 sm:gap-6 min-w-max">
          <button
            type="button"
            onClick={() => setActiveTab('invoices')}
            className={`py-3 px-1 border-b-2 text-sm font-medium min-h-[44px] ${
              activeTab === 'invoices'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Invoices
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('aging')}
            className={`py-3 px-1 border-b-2 text-sm font-medium flex items-center gap-1 min-h-[44px] ${
              activeTab === 'aging'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ClockIcon className="w-4 h-4" />
            AR Aging
          </button>
        </nav>
      </div>

      {activeTab === 'aging' ? (
        <>
          <div className="native-mobile-list space-y-2">
            {agingByCustomer.length === 0 ? (
              <MobileEmptyState
                icon={<ClockIcon className="w-12 h-12" />}
                title="No outstanding balances"
              />
            ) : (
              agingByCustomer.map((b) => (
                <MobileListRow
                  key={b.customerId}
                  onClick={() => navigate(`/customers/view/${b.customerId}`)}
                  title={b.name}
                  trailing={
                    <span className="text-sm font-bold text-gray-900 tabular-nums flex-shrink-0">
                      {money(b.total)}
                    </span>
                  }
                  chevron={false}
                  meta={
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                      <span>Current: {money(b.current)}</span>
                      <span className="text-yellow-700">1–30: {money(b.d1_30)}</span>
                      <span className="text-orange-700">31–60: {money(b.d31_60)}</span>
                      <span className="text-red-600">61–90: {money(b.d61_90)}</span>
                      <span className="text-red-800 col-span-2">90+: {money(b.d90_plus)}</span>
                    </div>
                  }
                />
              ))
            )}
          </div>
          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Accounts Receivable Aging</h2>
              <p className="text-sm text-gray-500 mt-0.5">Outstanding balances by customer, aged from due date</p>
            </div>
            {agingByCustomer.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <div className="text-gray-500">No outstanding balances</div>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Current</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">1-30</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">31-60</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">61-90</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">90+</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {agingByCustomer.map((b) => (
                      <tr key={b.customerId} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{b.name}</td>
                        <td className="px-3 py-3 text-sm text-gray-700 text-right">{money(b.current)}</td>
                        <td className="px-3 py-3 text-sm text-yellow-700 text-right">{money(b.d1_30)}</td>
                        <td className="px-3 py-3 text-sm text-orange-700 text-right">{money(b.d31_60)}</td>
                        <td className="px-3 py-3 text-sm text-red-600 text-right">{money(b.d61_90)}</td>
                        <td className="px-3 py-3 text-sm text-red-800 font-semibold text-right">{money(b.d90_plus)}</td>
                        <td className="px-3 py-3 text-sm font-bold text-gray-900 text-right">{money(b.total)}</td>
                        <td className="px-3 py-3 text-sm">
                          <button
                            onClick={() => navigate(`/customers/view/${b.customerId}`)}
                            className="text-blue-600 hover:text-blue-900 text-xs font-medium"
                          >
                            View Customer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-3 py-3 text-sm font-bold text-gray-900">Total</td>
                      <td className="px-3 py-3 text-sm font-bold text-gray-900 text-right">{money(agingTotals.current)}</td>
                      <td className="px-3 py-3 text-sm font-bold text-yellow-700 text-right">{money(agingTotals.d1_30)}</td>
                      <td className="px-3 py-3 text-sm font-bold text-orange-700 text-right">{money(agingTotals.d31_60)}</td>
                      <td className="px-3 py-3 text-sm font-bold text-red-600 text-right">{money(agingTotals.d61_90)}</td>
                      <td className="px-3 py-3 text-sm font-bold text-red-800 text-right">{money(agingTotals.d90_plus)}</td>
                      <td className="px-3 py-3 text-sm font-bold text-gray-900 text-right">{money(agingTotals.total)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="native-mobile-list space-y-2">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredInvoices.length === 0 ? (
              <MobileEmptyState
                icon={<FileTextIcon className="w-12 h-12" />}
                title={searchTerm || statusFilter !== 'all' ? 'No invoices match your filters' : 'No invoices yet'}
                description={!searchTerm && statusFilter === 'all' ? 'Create your first invoice from Invoice Generation' : undefined}
                action={
                  !searchTerm && statusFilter === 'all' ? (
                    <button type="button" onClick={() => navigate('/invoice-generation')} className={mobile.primaryBtn}>
                      Create Invoice
                    </button>
                  ) : undefined
                }
              />
            ) : (
              filteredInvoices.map((inv) => (
                <MobileListRow
                  key={inv.id}
                  onClick={() => navigate(`/invoice-generation?invoice=${inv.id}`)}
                  title={inv.invoice_number}
                  subtitle={getCustomerName(inv)}
                  badges={
                    <span className={`${mobile.badge} ${getInvoiceStatusColor(inv.status)}`}>
                      {getInvoiceStatusLabel(inv.status)}
                    </span>
                  }
                  meta={
                    <>
                      <div className="flex justify-between gap-2">
                        <span>Total {fmtMoney(inv.total)}</span>
                        <span className={inv.balance_due > 0 ? 'text-red-700 font-semibold' : 'text-green-700 font-semibold'}>
                          Bal {fmtMoney(inv.balance_due)}
                        </span>
                      </div>
                      <div>Due {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}</div>
                    </>
                  }
                  footer={
                    <>
                      {inv.status !== 'paid' && (
                        <MobileRowAction tone="success" onClick={() => navigate(`/payments?invoiceId=${inv.id}`)}>
                          <CreditCardIcon className="w-4 h-4" />
                          Pay
                        </MobileRowAction>
                      )}
                      <MobileRowAction onClick={() => navigate(`/invoice-generation?invoice=${inv.id}`)}>
                        <EyeIcon className="w-4 h-4" />
                        View
                      </MobileRowAction>
                      <MobileRowAction onClick={() => handleDownloadInvoicePdf(inv)}>
                        <DownloadIcon className="w-4 h-4" />
                        PDF
                      </MobileRowAction>
                    </>
                  }
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Invoices</h2>
            </div>
            {loading ? (
              <div className="px-6 py-12 text-center">
                <Loader2Icon className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                <div className="text-gray-500">Loading invoices...</div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <FileTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <div className="text-gray-500 mb-2">
                  {searchTerm || statusFilter !== 'all' ? 'No invoices match your filters' : 'No invoices yet'}
                </div>
                <p className="text-sm text-gray-400">
                  {!searchTerm && statusFilter === 'all' && 'Create your first invoice from Invoice Generation'}
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <button
                    onClick={() => navigate('/invoice-generation')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Create Invoice
                  </button>
                )}
              </div>
            ) : (
              <div className="w-full">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      {visibleColumns.has('invoice_number') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                      )}
                      {visibleColumns.has('customer') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      )}
                      {visibleColumns.has('amount') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      )}
                      {visibleColumns.has('paid') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                      )}
                      {visibleColumns.has('balance') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                      )}
                      {visibleColumns.has('status') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      )}
                      {visibleColumns.has('due_date') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      )}
                      {visibleColumns.has('actions') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        {visibleColumns.has('invoice_number') && (
                          <td className="px-3 py-3 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{inv.invoice_number}</div>
                            {inv.notes && <div className="text-xs text-gray-500 max-w-xs truncate">{inv.notes}</div>}
                          </td>
                        )}
                        {visibleColumns.has('customer') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            {getCustomerName(inv)}
                          </td>
                        )}
                        {visibleColumns.has('amount') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900 font-semibold">
                            {fmtMoney(inv.total)}
                          </td>
                        )}
                        {visibleColumns.has('paid') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-green-700">
                            {fmtMoney(inv.amount_paid)}
                          </td>
                        )}
                        {visibleColumns.has('balance') && (
                          <td className="px-3 py-3 min-w-0 text-sm font-semibold">
                            <span className={inv.balance_due > 0 ? 'text-red-700' : 'text-green-700'}>
                              {fmtMoney(inv.balance_due)}
                            </span>
                          </td>
                        )}
                        {visibleColumns.has('status') && (
                          <td className="px-3 py-3 min-w-0" title="Status is set automatically from payments and due date">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInvoiceStatusColor(inv.status)}`}
                              role="text"
                            >
                              {getInvoiceStatusLabel(inv.status)}
                            </span>
                          </td>
                        )}
                        {visibleColumns.has('due_date') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                          </td>
                        )}
                        {visibleColumns.has('actions') && (
                          <td className="px-3 py-3 min-w-0 text-sm font-medium">
                            <div className="flex gap-2">
                              {inv.status !== 'paid' && (
                                <button
                                  onClick={() => navigate(`/payments?invoiceId=${inv.id}`)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Record Payment"
                                >
                                  <CreditCardIcon className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => navigate(`/invoice-generation?invoice=${inv.id}`)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Invoice"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => navigate(`/invoice-generation?invoice=${inv.id}`)}
                                className="text-gray-600 hover:text-gray-900"
                                title="Edit"
                              >
                                <EditIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadInvoicePdf(inv)}
                                className="text-green-600 hover:text-green-900"
                                title="Download PDF"
                              >
                                <DownloadIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </MobilePage>
  )
}
